import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, AlertTriangle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Live countdown timer for an active conversation. Polls the backend every 30s
 * to keep extension/credit data fresh. When time hits zero, navigates the user
 * to /precios with reason=time_expired so they can purchase +20 min.
 */
export default function CallTimer({ conversationId, onExpired }) {
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState(null);
  const [credits, setCredits] = useState(0);
  const [extension, setExtension] = useState(0);
  const [extending, setExtending] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const expiredHandledRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/conversations/${conversationId}/time`);
      setRemaining(Number(data.remaining_seconds));
      setCredits(Number(data.user_time_credits_minutes || 0));
      setExtension(Number(data.extension_minutes || 0));
    } catch (err) {
      // Polling errors are expected during transient network blips. Log only in dev.
      if (process.env.NODE_ENV !== "production") {
        if (process.env.NODE_ENV === "development") {
          console.warn("CallTimer poll failed (non-critical):", err?.message);
        }
      }
    }
  }, [conversationId]);

  // initial + periodic sync (every 30s)
  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // local 1-second countdown between syncs
  useEffect(() => {
    if (remaining === null) return;
    if (remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((r) => (r === null ? null : Math.max(0, r - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [remaining]);

  // handle expiration once
  useEffect(() => {
    if (remaining !== 0 || expiredHandledRef.current) return;
    expiredHandledRef.current = true;
    onExpired && onExpired();
    // Generate closing summary in background (fire-and-forget). The user will see it
    // on the Dashboard ResumeCallCard next time they open it.
    if (conversationId) {
      api.post(`/conversations/${conversationId}/summary`).catch(() => {
        /* non-blocking — user can re-trigger from dashboard */
      });
    }
    toast.error("Tu llamada de 20 minutos terminó. Generando resumen para tu próxima sesión...");
    setTimeout(() => navigate(`/precios?reason=time_expired&plan=extra_time`), 2000);
  }, [remaining, navigate, onExpired, conversationId]);

  // 3-min early warning (minuto 17): show a one-time non-blocking modal
  useEffect(() => {
    if (remaining === null) return;
    if (warningShown) return;
    if (remaining <= 180 && remaining > 30) {
      setWarningShown(true);
      setShowWarningModal(true);
    }
  }, [remaining, warningShown]);

  const handleExtend = async () => {
    setExtending(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/extend`);
      setRemaining(Number(data.remaining_seconds));
      setExtension(Number(data.extension_minutes || 0));
      expiredHandledRef.current = false;
      toast.success("¡Llamada extendida 20 minutos!");
      await fetchStatus();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("No tienes minutos de extensión. Compra 20 minutos por $8.");
        navigate(`/precios?reason=time_expired&plan=extra_time`);
      } else {
        toast.error("No se pudo extender la llamada");
      }
    } finally {
      setExtending(false);
    }
  };

  if (remaining === null) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const isWarning = remaining <= 120; // last 2 min
  const isCritical = remaining <= 30; // last 30s

  // Pure helpers — replace nested ternaries with clear lookups
  const containerClass = getContainerClass(isCritical, isWarning);
  const iconBgClass = getIconBgClass(isCritical, isWarning);
  const displayClass = getDisplayClass(isCritical, isWarning);

  return (
    <div
      data-testid="call-timer"
      className={`flex items-center gap-3 rounded-xl px-3 py-2 border-2 ${containerClass}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBgClass}`}>
        {isCritical ? (
          <AlertTriangle className="w-5 h-5 text-white" strokeWidth={2.5} />
        ) : (
          <Clock className="w-5 h-5 text-white" strokeWidth={2.5} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider font-black text-zinc-500 leading-none mb-0.5">
          Tiempo restante {extension > 0 ? `· +${extension} min extra` : ""}
        </p>
        <p
          data-testid="call-timer-display"
          className={`text-xl font-black font-mono leading-tight ${displayClass}`}
        >
          {mm}:{ss}
        </p>
      </div>
      {isWarning && credits > 0 && (
        <Button
          data-testid="call-timer-extend"
          onClick={handleExtend}
          disabled={extending}
          size="sm"
          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          +20 min
        </Button>
      )}
      {isWarning && credits === 0 && (
        <Button
          data-testid="call-timer-buy"
          onClick={() => navigate("/precios?reason=time_expired&plan=extra_time")}
          size="sm"
          className="h-9 px-3 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs rounded-lg"
        >
          Comprar +20 min · $8
        </Button>
      )}

      {showWarningModal && (
        <div
          data-testid="call-timer-warning-modal"
          className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowWarningModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-2xl border-2 border-amber-300"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-950" strokeWidth={2.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-black text-amber-700">
                  Aviso del sistema
                </p>
                <p className="text-base font-black text-zinc-900">Te quedan 3 minutos</p>
              </div>
            </div>
            <p className="text-sm text-zinc-700 leading-snug mb-4">
              Tu llamada de 20 minutos está por terminar. Para no perder el hilo de tu formulario, puedes comprar 20 minutos extra por solo <span className="font-black text-zinc-900">$8 USD</span>. La IA recordará todo lo que ya hablamos.
            </p>
            {credits > 0 ? (
              <Button
                data-testid="call-timer-warning-extend-now"
                onClick={async () => {
                  setShowWarningModal(false);
                  await handleExtend();
                }}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl mb-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Usar 20 min de mis créditos
              </Button>
            ) : (
              <Button
                data-testid="call-timer-warning-buy"
                onClick={() => navigate("/precios?reason=time_expired&plan=extra_time")}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black rounded-xl mb-2"
              >
                Comprar +20 min por $8 ahora
              </Button>
            )}
            <Button
              data-testid="call-timer-warning-dismiss"
              variant="ghost"
              onClick={() => setShowWarningModal(false)}
              className="w-full h-10 text-zinc-600 font-bold rounded-xl"
            >
              Seguir sin extender
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pure style helpers — keep render path linear (no nested ternaries) ----
function getContainerClass(isCritical, isWarning) {
  if (isCritical) return "bg-red-50 border-red-400 animate-pulse";
  if (isWarning) return "bg-amber-50 border-amber-400";
  return "bg-zinc-50 border-zinc-200";
}

function getIconBgClass(isCritical, isWarning) {
  if (isCritical) return "bg-red-600";
  if (isWarning) return "bg-amber-500";
  return "bg-zinc-700";
}

function getDisplayClass(isCritical, isWarning) {
  if (isCritical) return "text-red-700";
  if (isWarning) return "text-amber-800";
  return "text-zinc-900";
}
